/**
 * MyBookings.jsx
 * Customer-facing "My Bookings" page (Session 32 pagination rollout — letter b).
 *
 * Lists every session the signed-in user has booked (requested, confirmed,
 * completed, cancelled) using paginated GET /api/bookings (25/page,
 * newest-first by scheduled_at desc).
 *
 * Design: muted forest + gold to match BookingsPortal so the two pages feel
 * like one journey ("Browse" → "My Bookings").
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar, Clock, CalendarCheck, ArrowLeft, Loader, X, Check, Video, Star,
  CalendarClock,
} from "lucide-react";
import { apiFetch } from "./api";
import { Pagination } from "../../components/Pagination";
import PendingReviewsBanner from "@/components/PendingReviewsBanner";
import IntakeFormsBlock from "@/components/marketplace/IntakeFormsBlock";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Crimson+Pro:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Outfit:wght@300;400;500;600&display=swap');

  .mb-root {
    --forest:      #1b3a2d;
    --forest-mid:  #2d5a46;
    --forest-light:#4a8a68;
    --gold:        #c8a84a;
    --gold-bg:     #faf5e8;
    --cream:       #f8f5ee;
    --border:      #ddd8cc;
    --ink:         #1a1a18;
    --ink-mid:     #4a4a46;
    --ink-light:   #8a8a86;
    --surface:     #ffffff;
    --amber:       #9a6010;
    --amber-bg:    #fef9ea;
    --green-bg:    #eaf5ea;
    --green:       #2a6a3a;
    --red:         #a02828;
    --red-bg:      #fdf0f0;
    font-family: 'Outfit', sans-serif;
    background: var(--cream);
    color: var(--ink);
    min-height: 100vh;
  }

  .mb-header {
    background: var(--surface);
    border-bottom: 1px solid var(--border);
    padding: 20px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    position: sticky;
    top: 0;
    z-index: 20;
  }
  .mb-header h1 {
    font-family: 'Crimson Pro', serif;
    font-size: 22px;
    font-weight: 600;
    color: var(--forest);
    margin: 0 0 2px;
  }
  .mb-header p { font-size: 13px; color: var(--ink-light); margin: 0; }
  .mb-header-back {
    display: inline-flex; align-items: center; gap: 6px;
    color: var(--ink-mid); text-decoration: none; font-size: 13px;
    padding: 8px 14px; border: 1px solid var(--border); border-radius: 10px;
    background: var(--surface); transition: background 0.15s;
  }
  .mb-header-back:hover { background: var(--cream); }

  .mb-list { padding: 16px 24px 40px; display: flex; flex-direction: column; gap: 12px; }

  .mb-empty {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 16px;
    padding: 60px 28px;
    text-align: center;
  }
  .mb-empty h3 {
    font-family: 'Crimson Pro', serif;
    font-size: 22px;
    color: var(--ink);
    margin: 18px 0 6px;
    font-weight: 600;
  }
  .mb-empty p {
    font-size: 14px; color: var(--ink-mid); margin: 0 0 18px;
    line-height: 1.55; max-width: 340px; margin-left: auto; margin-right: auto;
  }

  .mb-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 16px;
    padding: 18px 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .mb-card-top {
    display: flex; justify-content: space-between; align-items: flex-start; gap: 16px;
  }
  .mb-card-title {
    font-family: 'Crimson Pro', serif; font-size: 18px; font-weight: 600;
    color: var(--ink); margin: 0;
  }
  .mb-card-vendor {
    font-size: 13px; color: var(--ink-light); margin: 2px 0 0;
  }
  .mb-status {
    flex-shrink: 0;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  .mb-s-requested { background: var(--amber-bg); color: var(--amber); border: 1px solid #e8d080; }
  .mb-s-confirmed { background: var(--green-bg); color: var(--green); border: 1px solid #b8d8c0; }
  .mb-s-completed { background: #eef4fd; color: #1a50a0; border: 1px solid #b0cce8; }
  .mb-s-cancelled { background: var(--red-bg); color: var(--red); border: 1px solid #e8b0b0; }
  .mb-s-declined  { background: var(--red-bg); color: var(--red); border: 1px solid #e8b0b0; }

  .mb-card-row {
    display: flex; gap: 16px; flex-wrap: wrap; align-items: center;
    color: var(--ink-mid); font-size: 13px;
  }
  .mb-card-row span { display: inline-flex; align-items: center; gap: 6px; }
  .mb-price { font-weight: 700; color: var(--forest); }

  .mb-loading {
    display: flex; align-items: center; justify-content: center;
    padding: 80px 0; color: var(--ink-light); gap: 8px; font-size: 14px;
  }
  @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
`;

const STATUS_LABEL = {
  requested: "Awaiting confirmation",
  confirmed: "Confirmed",
  completed: "Completed",
  cancelled: "Cancelled",
  declined:  "Declined",
};

const STATUS_CLASS = {
  requested: "mb-s-requested",
  confirmed: "mb-s-confirmed",
  completed: "mb-s-completed",
  cancelled: "mb-s-cancelled",
  declined:  "mb-s-declined",
};

function formatScheduled(value, tz) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    const opts = { dateStyle: "medium", timeStyle: "short" };
    if (tz) opts.timeZone = tz;
    return d.toLocaleString(undefined, opts);
  } catch {
    return value;
  }
}

function BookingCard({ b, onCancelled }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy]       = useState(false);
  const [status, setStatus]   = useState(b.status);
  const [offer, setOffer]     = useState(b.reschedule_offer || null);
  const [request, setRequest] = useState(b.reschedule_request || null);
  const [when, setWhen]       = useState(b.scheduled_at);
  const [askingTime, setAskingTime] = useState(false);

  const money = n => `$${Number(n || 0).toFixed(2)}`;
  const canCancel = ["requested", "pending", "confirmed"].includes(status);

  const acceptTime = async (iso) => {
    setBusy(true);
    try {
      const res = await apiFetch(`/bookings/${b.id}/reschedule/accept`, {
        method: "POST", body: JSON.stringify({ scheduled_at: iso }),
      });
      setWhen(res.scheduled_at);
      setOffer(null);
    } catch (err) {
      alert(err.message || "We couldn't move your session to that time.");
    } finally {
      setBusy(false);
    }
  };

  const declineOffer = async () => {
    setBusy(true);
    try {
      await apiFetch(`/bookings/${b.id}/reschedule/decline`, { method: "POST" });
      setOffer(null);
    } catch (err) {
      alert(err.message || "We couldn't decline that.");
    } finally {
      setBusy(false);
    }
  };

  const requestNewTime = async (note) => {
    setBusy(true);
    try {
      const res = await apiFetch(`/bookings/${b.id}/reschedule/request`, {
        method: "POST", body: JSON.stringify({ note: note || null }),
      });
      setRequest(res.reschedule_request);
      setAskingTime(false);
    } catch (err) {
      alert(err.message || "We couldn't send that request.");
    } finally {
      setBusy(false);
    }
  };

  const askCancel = async () => {
    setBusy(true);
    try {
      setPreview(await apiFetch(`/bookings/${b.id}/cancellation-preview`));
    } catch {
      setPreview({ unavailable: true });
    } finally {
      setBusy(false);
    }
  };

  const confirmCancel = async () => {
    setBusy(true);
    try {
      // The endpoint is a PATCH — POSTing it returned a browser "Method Not Allowed" alert
      await apiFetch(`/bookings/${b.id}/cancel`, { method: "PATCH" });
      setStatus("cancelled");
      setPreview(null);
      onCancelled?.(b.id);
    } catch (err) {
      alert(err.message || "We couldn't cancel that session. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const statusClass = STATUS_CLASS[status] || "mb-s-confirmed";
  const statusLabel = STATUS_LABEL[status] || status;
  return (
    <div className="mb-card" data-testid={`my-booking-${b.id}`}>
      <div className="mb-card-top">
        <div style={{ minWidth: 0 }}>
          <h3 className="mb-card-title">
            {b.session_type_name || b.session_title || b.session_type_id || "Session"}
          </h3>
          {(b.practitioner_name || b.vendor_name) && (
            <p className="mb-card-vendor" data-testid={`my-booking-practitioner-${b.id}`}>
              with {b.practitioner_name || b.vendor_name}
            </p>
          )}
        </div>
        <span className={`mb-status ${statusClass}`}>{statusLabel}</span>
      </div>

      <div className="mb-card-row">
        <span><CalendarCheck size={14} /> {formatScheduled(when, b.timezone)}</span>
        {b.duration_minutes && (
          <span><Clock size={14} /> {b.duration_minutes} min</span>
        )}
        {b.mode && (
          <span><Video size={14} /> {b.mode.replace("_", " ")}</span>
        )}
        <span className="mb-price" data-testid={`my-booking-price-${b.id}`}>
          {Number(b.price) > 0 ? `$${Number(b.price).toFixed(2)}` : "Free"}
        </span>
      </div>

      {b.charge_breakdown && (
        <div
          style={{ fontSize: 12, color: "var(--charcoal-light)", marginTop: -2 }}
          data-testid={`my-booking-charge-breakdown-${b.id}`}
        >
          {b.charge_breakdown.state === "paid" ? "You paid " : "Held on your card: "}
          ${b.charge_breakdown.total_charged.toFixed(2)}
          {" (session $"}
          {b.charge_breakdown.price.toFixed(2)}
          {b.charge_breakdown.tax > 0 && ` + $${b.charge_breakdown.tax.toFixed(2)} sales tax`}
          {` + $${b.charge_breakdown.processing_fee.toFixed(2)} card processing, ${b.charge_breakdown.fee_label})`}
          {b.charge_breakdown.state !== "paid" && " — charged once your practitioner confirms"}
        </div>
      )}
      {status !== "cancelled" && (
        <IntakeFormsBlock forms={b.intake_forms} testId={`my-booking-intake-${b.id}`} compact />
      )}

      {/* Where to be. The link lives here, not only in an email, so it can't be lost. */}
      {b.location && status !== "cancelled" && (
        <div style={{ fontSize: 13, color: "var(--charcoal)", marginTop: 4, lineHeight: 1.6 }}
             data-testid={`my-booking-location-${b.id}`}>
          <strong style={{ fontSize: 12, color: "var(--charcoal-light)" }}>{b.location.label}</strong>
          {b.location.link && (
            <div style={{ marginTop: 4 }}>
              <a href={b.location.link} target="_blank" rel="noreferrer"
                 style={{
                   display: "inline-flex", alignItems: "center", gap: 6,
                   background: "var(--forest)", color: "#fff", padding: "7px 16px",
                   borderRadius: 999, fontSize: 13, fontWeight: 600, textDecoration: "none",
                 }}
                 data-testid={`my-booking-join-${b.id}`}>
                <Video size={13} /> Join session
              </a>
            </div>
          )}
          {b.location.address && (
            <div style={{ whiteSpace: "pre-wrap", marginTop: 2 }}>{b.location.address}</div>
          )}
          {b.location.note && (
            <div style={{ whiteSpace: "pre-wrap", marginTop: 2, color: "var(--charcoal-light)" }}>
              {b.location.note}
            </div>
          )}
          {b.location.pending_text && (
            <div style={{ marginTop: 2, color: "var(--charcoal-light)" }}>{b.location.pending_text}</div>
          )}
        </div>
      )}

      {canCancel && !offer && !request && (
        <button
          onClick={() => setAskingTime(true)}
          disabled={busy}
          style={{
            alignSelf: "flex-start", marginTop: 8,
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "transparent", color: "var(--forest)",
            border: "1px solid var(--forest)", padding: "8px 18px",
            borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer",
            fontFamily: "inherit", opacity: busy ? 0.6 : 1,
          }}
          data-testid={`request-new-time-btn-${b.id}`}
        >
          <CalendarClock size={13} /> Request a new time
        </button>
      )}

      {request && (
        <div
          style={{
            marginTop: 10, padding: "11px 14px", borderRadius: 10,
            background: "var(--amber-bg)", color: "var(--amber)",
            fontSize: 13, lineHeight: 1.6,
          }}
          data-testid={`reschedule-request-sent-${b.id}`}
        >
          You&apos;ve asked for a new time — your practitioner will come back with options.
        </div>
      )}

      {offer && (
        <div
          style={{
            marginTop: 10, padding: "14px 16px", borderRadius: 10,
            background: "var(--green-bg)", border: "1px solid var(--green)",
          }}
          data-testid={`reschedule-offer-${b.id}`}
        >
          <p style={{ fontSize: 13, fontWeight: 600, color: "var(--forest)", marginBottom: 4 }}>
            Your practitioner offered a new time
          </p>
          {offer.message && (
            <p style={{ fontSize: 13, fontStyle: "italic", color: "var(--ink-mid)", marginBottom: 8 }}>
              &ldquo;{offer.message}&rdquo;
            </p>
          )}
          <p style={{ fontSize: 12, color: "var(--ink-light)", marginBottom: 10 }}>
            Pick one and your session moves across — you won&apos;t be charged again.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {(offer.times || []).map((t, i) => (
              <button
                key={t}
                onClick={() => acceptTime(t)}
                disabled={busy}
                style={{
                  textAlign: "left", padding: "10px 14px", borderRadius: 9,
                  border: "1px solid var(--green)", background: "#fff",
                  color: "var(--ink)", fontSize: 13, fontWeight: 600,
                  cursor: "pointer", fontFamily: "inherit", opacity: busy ? 0.6 : 1,
                }}
                data-testid={`accept-time-${b.id}-${i}`}
              >
                {formatScheduled(t, b.timezone)}
              </button>
            ))}
          </div>
          <button
            onClick={declineOffer}
            disabled={busy}
            style={{
              marginTop: 10, background: "transparent", border: "none",
              color: "var(--ink-light)", fontSize: 12, textDecoration: "underline",
              cursor: "pointer", fontFamily: "inherit", padding: 0,
            }}
            data-testid={`decline-offer-${b.id}`}
          >
            None of these work
          </button>
        </div>
      )}

      {askingTime && (
        <RequestTimeDialog
          busy={busy}
          onClose={() => setAskingTime(false)}
          onSubmit={requestNewTime}
        />
      )}

      {canCancel && (
        <button
          onClick={askCancel}
          disabled={busy}
          style={{
            alignSelf: "flex-start", marginTop: 8,
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "transparent", color: "var(--red)",
            border: "1px solid var(--red)", padding: "8px 18px",
            borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer",
            fontFamily: "inherit", opacity: busy ? 0.6 : 1,
          }}
          data-testid={`cancel-booking-btn-${b.id}`}
        >
          <X size={13} /> Cancel session
        </button>
      )}

      {preview && (
        <ClientCancelDialog
          preview={preview}
          busy={busy}
          money={money}
          onKeep={() => setPreview(null)}
          onConfirm={confirmCancel}
        />
      )}

      {status === "completed" && (
        <Link
          to={`/dashboard/bookings/review/${b.id}`}
          style={{
            alignSelf: "flex-start", marginTop: 8,
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "var(--forest)", color: "#fff",
            padding: "9px 20px", borderRadius: 999,
            fontSize: 13, fontWeight: 600, textDecoration: "none",
          }}
          data-testid={`leave-review-link-${b.id}`}
        >
          <Star size={13} /> {b.reviewed ? "Leave a tip" : "Leave a review or tip"}
        </Link>
      )}
    </div>
  );
}



/** Client asks for a different time instead of cancelling and losing the fee. */
function RequestTimeDialog({ busy, onClose, onSubmit }) {
  const [note, setNote] = useState("");
  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 90, background: "rgba(20,24,20,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}
      data-testid="request-time-dialog"
    >
      <div style={{
        background: "#fff", borderRadius: 16, maxWidth: 440, width: "100%", padding: 26,
        boxShadow: "0 24px 60px rgba(0,0,0,0.3)", fontFamily: "'Outfit', sans-serif",
      }}>
        <h3 style={{
          fontFamily: "'Crimson Pro', serif", fontSize: 21, fontWeight: 600,
          color: "var(--forest)", margin: "0 0 10px",
        }}>
          Ask for a different time
        </h3>
        <p style={{ fontSize: 14, color: "var(--ink-mid)", lineHeight: 1.7 }}>
          Your practitioner will come back with times that work for them. Moving your
          session costs you nothing — cancelling might.
        </p>
        <textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          rows={3}
          placeholder="Anything that helps them — mornings are better, away next week, and so on"
          style={{
            width: "100%", marginTop: 14, padding: "10px 12px", borderRadius: 10,
            border: "1px solid var(--border)", fontSize: 14, fontFamily: "inherit",
            color: "var(--ink)", background: "#fff", resize: "vertical",
          }}
          data-testid="request-time-note"
        />
        <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: "11px 16px", borderRadius: 999, fontWeight: 600,
              fontSize: 13, border: "1px solid var(--border)", background: "var(--cream)",
              color: "var(--ink)", cursor: "pointer", fontFamily: "inherit",
            }}
            data-testid="request-time-cancel-btn"
          >
            Never mind
          </button>
          <button
            onClick={() => onSubmit(note)}
            disabled={busy}
            style={{
              flex: 1, padding: "11px 16px", borderRadius: 999, fontWeight: 600,
              fontSize: 13, border: "none", background: "var(--forest)", color: "#fff",
              cursor: "pointer", fontFamily: "inherit", opacity: busy ? 0.6 : 1,
            }}
            data-testid="request-time-send-btn"
          >
            {busy ? "Sending…" : "Send request"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** What the client will get back, in their practitioner's own words, before they commit. */
function ClientCancelDialog({ preview, busy, money, onKeep, onConfirm }) {
  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 90, background: "rgba(20,24,20,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}
      data-testid="client-cancel-dialog"
    >
      <div style={{
        background: "#fff", borderRadius: 16, maxWidth: 460, width: "100%", padding: 26,
        boxShadow: "0 24px 60px rgba(0,0,0,0.3)", fontFamily: "'Outfit', sans-serif",
      }}>
        <h3 style={{
          fontFamily: "'Crimson Pro', serif", fontSize: 21, fontWeight: 600,
          color: "var(--forest)", margin: "0 0 12px",
        }}>
          Cancel this session?
        </h3>

        {preview.unavailable ? (
          <p style={{ fontSize: 14, color: "var(--ink-mid)", lineHeight: 1.6 }}>
            We couldn&apos;t work out your refund just now. You can still cancel, and your
            practitioner&apos;s policy will be applied.
          </p>
        ) : !preview.captured ? (
          <p style={{ fontSize: 14, color: "var(--ink-mid)", lineHeight: 1.7 }}
             data-testid="client-cancel-body">
            You haven&apos;t been charged for this session yet — cancelling simply releases the
            hold on your card.
          </p>
        ) : (
          <>
            <p style={{ fontSize: 14, color: "var(--ink-mid)", lineHeight: 1.7 }}
               data-testid="client-cancel-body">
              You paid <strong style={{ color: "var(--ink)" }}>{money(preview.client_paid)}</strong>{" "}
              for this session. Cancelling now returns{" "}
              <strong style={{ color: "var(--ink)" }}>{money(preview.client_refund)}</strong>.
            </p>
            <div style={{
              marginTop: 14, padding: "12px 14px", borderRadius: 10,
              background: preview.client_refund > 0 ? "var(--green-bg)" : "var(--amber-bg)",
              color: preview.client_refund > 0 ? "var(--green)" : "var(--amber)",
              fontSize: 13, lineHeight: 1.6,
            }} data-testid="client-cancel-policy">
              {preview.explanation}
            </div>
            <p style={{ fontSize: 12, color: "var(--ink-light)", marginTop: 10, lineHeight: 1.6 }}>
              Your practitioner&apos;s policy: full refund more than{" "}
              {preview.policy?.full_refund_hours}h ahead,{" "}
              {preview.policy?.partial_refund_percent}% between{" "}
              {preview.policy?.full_refund_hours}h and{" "}
              {preview.policy?.partial_refund_hours}h, none inside{" "}
              {preview.policy?.partial_refund_hours}h. You have{" "}
              {preview.hours_notice}h of notice.
            </p>
          </>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
          <button
            onClick={onKeep}
            style={{
              flex: 1, padding: "11px 16px", borderRadius: 999, fontWeight: 600, fontSize: 13,
              border: "1px solid var(--border)", background: "var(--cream)",
              color: "var(--ink)", cursor: "pointer", fontFamily: "inherit",
            }}
            data-testid="client-cancel-keep-btn"
          >
            Keep my session
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            style={{
              flex: 1, padding: "11px 16px", borderRadius: 999, fontWeight: 600, fontSize: 13,
              border: "none", background: "var(--red)", color: "#fff", cursor: "pointer",
              fontFamily: "inherit", opacity: busy ? 0.6 : 1,
            }}
            data-testid="client-cancel-confirm-btn"
          >
            {busy ? "Cancelling…" : "Cancel session"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MyBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const PER_PAGE = 25;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiFetch(`/bookings?page=${page}&per_page=${PER_PAGE}`)
      .then((resp) => {
        if (cancelled) return;
        setBookings(resp?.bookings || []);
        setTotalPages(resp?.total_pages || 1);
        setTotal(resp?.total || 0);
      })
      .catch(() => {
        if (!cancelled) {
          setBookings([]);
          setTotalPages(1);
          setTotal(0);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page]);

  return (
    <>
      <style>{styles}</style>
      <div className="mb-root" data-testid="my-bookings-page">
        <div className="mb-header">
          <div>
            <h1>My Bookings</h1>
            <p>{total} {total === 1 ? "session" : "sessions"} on your calendar</p>
          </div>
          <Link to="/dashboard/bookings" className="mb-header-back" data-testid="my-bookings-browse-link">
            <ArrowLeft size={14} /> Browse practitioners
          </Link>
        </div>

        <div className="mb-list">
          <PendingReviewsBanner />
          {loading ? (
            <div className="mb-loading">
              <Loader size={18} style={{ animation: "spin 1s linear infinite" }} />
              Loading your bookings…
            </div>
          ) : bookings.length === 0 ? (
            <div className="mb-empty">
              <Calendar size={36} style={{ color: "#8a8a86" }} />
              <h3>No bookings yet</h3>
              <p>
                When you book a session with a practitioner, it&apos;ll show up
                here so you can review the schedule, cancel, or leave a tip
                after the session.
              </p>
              <Link
                to="/dashboard/bookings"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  padding: "12px 24px", borderRadius: 11,
                  background: "var(--forest)", color: "white", textDecoration: "none",
                  fontSize: 14, fontWeight: 600,
                }}
                data-testid="my-bookings-empty-cta"
              >
                <Check size={14} /> Browse practitioners
              </Link>
            </div>
          ) : (
            <>
              {bookings.map((b) => (
                <BookingCard key={b.id} b={b} />
              ))}
              <Pagination
                page={page}
                totalPages={totalPages}
                onChange={setPage}
                testidPrefix="my-bookings-pagination"
                theme="light"
              />
            </>
          )}
        </div>
      </div>
    </>
  );
}
